/**
 * Reprise d'une séance en cours après un VRAI relancement de l'app.
 *
 * L'app installée sur l'écran d'accueil d'un iPhone est fermée par iOS dès qu'il manque
 * de mémoire — en quelques secondes parfois, le temps d'ouvrir Spotify. Au retour, ce
 * n'est pas un retour sur l'onglet (cas couvert par le correctif du 01/10, page restée
 * en mémoire) mais un démarrage à froid : l'app rouvre sur `start_url` (/dashboard) et
 * `sessionStorage` est vide. C'est là que vivaient la position dans la séance et les
 * chronos de repos : constaté le 17/10 sur la première séance d'un client, la séance
 * repartait de l'aperçu.
 *
 * Tout passe donc en `localStorage`, qui survit à la fermeture. Contrepartie : ce qui
 * y est écrit n'expire jamais de lui-même. Chaque entrée porte sa date d'écriture
 * (`savedAt`) et n'est plus relue au-delà de `RESUME_MAX_AGE_MS` ; les clés de chrono
 * portent l'identifiant de la séance (`<séance>|<repos>`) et sont effacées avec elle à
 * la fin de la séance.
 */

const PROGRESS_PREFIX = "zeyone_progress_"
const TIMER_PREFIX = "zeyone_timer_"

/** Au-delà, la séance est considérée abandonnée : on repart de l'aperçu. */
const RESUME_MAX_AGE_MS = 12 * 60 * 60 * 1000

/**
 * Démarrage à froid sur l'accueil : on ne renvoie d'office sur la séance que si elle a
 * bougé récemment — un retour d'une autre app, pas une séance laissée en plan le matin.
 */
const AUTO_RESUME_MAX_AGE_MS = 2 * 60 * 60 * 1000

/** Ancienne clé (sessionStorage, par séance seule) — relue une fois pour ne pas perdre une séance en cours au déploiement. */
const LEGACY_PROGRESS_PREFIX = "zeyone_progress_"

type Stamped = { savedAt?: number }

function progressKey(userId: string, sessionId: string): string {
  return `${PROGRESS_PREFIX}${userId}|${sessionId}`
}

function readStamped<T>(key: string, maxAgeMs: number): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const value = JSON.parse(raw) as T & Stamped
    if (typeof value.savedAt !== "number" || Date.now() - value.savedAt > maxAgeMs) return null
    return value
  } catch {
    return null
  }
}

function writeStamped(key: string, value: object): void {
  try {
    localStorage.setItem(key, JSON.stringify({ ...value, savedAt: Date.now() }))
  } catch {
    // mode privé / quota — dégrade en mémoire seule
  }
}

function removeKey(key: string): void {
  try { localStorage.removeItem(key) } catch { /* stockage indisponible */ }
}

function keysWithPrefix(prefix: string): string[] {
  const keys: string[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(prefix)) keys.push(k)
    }
  } catch { /* stockage indisponible */ }
  return keys
}

// ── Position dans la séance ─────────────────────────────────

export function loadSessionProgress<T extends object>(userId: string, sessionId: string): T | null {
  const saved = readStamped<T>(progressKey(userId, sessionId), RESUME_MAX_AGE_MS)
  if (saved) return saved
  try {
    const legacy = sessionStorage.getItem(LEGACY_PROGRESS_PREFIX + sessionId)
    return legacy ? (JSON.parse(legacy) as T) : null
  } catch {
    return null
  }
}

export function saveSessionProgress(userId: string, sessionId: string, progress: object): void {
  writeStamped(progressKey(userId, sessionId), progress)
}

/** Fin de séance : la position et tous les chronos de cette séance. */
export function clearSessionProgress(userId: string, sessionId: string): void {
  removeKey(progressKey(userId, sessionId))
  try { sessionStorage.removeItem(LEGACY_PROGRESS_PREFIX + sessionId) } catch { /* ignore */ }
  for (const k of keysWithPrefix(TIMER_PREFIX)) {
    if (k.includes(`:${sessionId}|`)) removeKey(k)
  }
}

/** Une séance de ce compte a-t-elle avancé (au-delà de l'aperçu) il y a peu ? */
export function hasRecentSessionInProgress(userId: string): boolean {
  const prefix = `${PROGRESS_PREFIX}${userId}|`
  return keysWithPrefix(prefix).some((k) => {
    const saved = readStamped<{ ph?: string }>(k, AUTO_RESUME_MAX_AGE_MS)
    return saved != null && saved.ph != null && saved.ph !== "overview" && saved.ph !== "done"
  })
}

/** Retire les entrées périmées (séances abandonnées, chronos jamais effacés). */
export function purgeExpiredSessionEntries(): void {
  for (const k of [...keysWithPrefix(PROGRESS_PREFIX), ...keysWithPrefix(TIMER_PREFIX)]) {
    if (readStamped(k, RESUME_MAX_AGE_MS) == null) removeKey(k)
  }
}

// ── Chronos de repos ────────────────────────────────────────
// `key` = `rest:<restKey>` ou `countdown:<restKey>`, où restKey commence par
// `<identifiant de séance>|` — c'est ce qui permet `clearSessionProgress`.

export function loadTimerState<T extends object>(key: string): T | null {
  return readStamped<T>(TIMER_PREFIX + key, RESUME_MAX_AGE_MS)
}

export function saveTimerState(key: string, state: object): void {
  writeStamped(TIMER_PREFIX + key, state)
}

export function removeTimerState(key: string): void {
  removeKey(TIMER_PREFIX + key)
}
